import React from 'react';

import type { TowerAttachmentPoints, TowerMode } from '../types';
import type { TowerConfidenceMap } from '../utils/towerConfidence';

/**
 * Uploads a temporary per-layer document and resolves to its temp doc id
 * (confirm-on-save is the backend's job — FPX-5831/5867). Refined when the
 * upload integration lands.
 */
export type TowerUploadHandler = (file: File) => Promise<string>;

/**
 * Resolves a previously-saved layer document's display name from its id, so a
 * reopened layer can show the real file name when the in-session File is gone.
 */
export type TowerDocNameResolver = (docId: string) => Promise<string | null>;

/**
 * Injected dependencies and derived state shared across the tower control
 * (FPX-5865). Lives in context so deeply-nested layer rows read `mode`,
 * `carrierOptions` and live `attachmentPoints` without prop-drilling.
 */
export interface TowerContextValue {
  readonly carrierOptions: readonly string[];
  readonly onUploadLayerDoc?: TowerUploadHandler;
  readonly mode: TowerMode;
  readonly attachmentPoints: TowerAttachmentPoints;
  // Per-leaf extraction confidence (0–100) for email-sourced towers, keyed by
  // dotted path; absent for manually-entered towers. Display-only metadata —
  // kept out of the form model so editing/validation are unaffected. (FPX-6331)
  readonly confidence?: TowerConfidenceMap;
  // Submission-tower only: show the primary layer's retention as its attachment
  // point (that is what it is) so it has a home for its confidence badge. Off for
  // the quote/UW tower, which keeps the derived $0 attachment. (FPX-6333)
  readonly primaryAttachmentAsRetention?: boolean;
}

const TowerContext = React.createContext<TowerContextValue | null>(null);

interface TowerProviderProps {
  readonly value: TowerContextValue;
  readonly children: React.ReactNode;
}

export const TowerProvider: React.FC<TowerProviderProps> = ({
  value,
  children,
}) => <TowerContext.Provider value={value}>{children}</TowerContext.Provider>;

export const useTowerContext = (): TowerContextValue => {
  const context = React.useContext(TowerContext);
  if (context == null) {
    throw new Error('useTowerContext must be used within a TowerProvider.');
  }
  return context;
};
